import {
  LiikkuvaWifiSalesProductOnlineModel,
  SalesProductOpeningFeeCpCodes,
} from '../../common/constants/subscriptionConstants';
import { MobileM2MAddonCode } from '../../common/enums';
import { getShoppingBasketFromLocalStorage, parseBasketJson } from '../../common/hooks/useShoppingBasket';
import {
  activationFeeWithVarMsg,
  activationFeesWithVarMsg,
  contractPriceCapitalizedMsg,
  deductibleMsg,
  monthlyChargesMsg,
  monthsMsg,
  noFixedTermMsg,
  oneTimePaymentMsg,
  recurringChargesMsg,
  singleFaresMsg,
  squareTradeDeductibleMsg,
  sumPerMonthMsg,
  t,
  totalWithCurlyBracketsMsg,
  vatPercentageMsg,
} from '../../common/i18n/index.js';
import {
  getEffectiveCharges,
  getM2MOpeningFee,
  getMobileBroadbandPricing,
  getSelfServiceSubscriptionPrice,
  isMobileBroadbandModel,
  isSquareTradeAddOn,
} from '../../common/utils/addOnUtils';
import { getOfferCampaignAssociationAndPromotion } from '../../common/utils/campaignUtils';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { formatSum, formatSumToString, isContractPrice } from '../../common/utils/priceUtils.js';
import { AddOnPurpose } from '../../generated/api/addOnPurpose';
import { CommercialProductSubType } from '../../generated/api/commercialProductSubType.js';
import { CommercialProductType } from '../../generated/api/commercialProductType.js';
import { EppSolutionResponse } from '../../generated/api/eppSolutionResponse';
import { OnlineModelCategory } from '../../generated/api/onlineModelCategory.js';
import { SalesType } from '../../generated/api/salesType';
import { areItemsEqualInBasket, getAddonsForOfferItem } from './shoppingBasketFunctions.js';
import { createOfferItemAddons, getBundledOfferAddons, isMobileM2MProduct } from './shoppingBasketHelpers';
import EppSolutionStatusEnum = EppSolutionResponse.EppSolutionStatusEnum;
import type { BasketItemState, ChangePaymentOptions, PaymentOptionData, PriceData, PriceType } from './types';
import type {
  CLShoppingCartAddon,
  CLShoppingCartPaymentOptions,
  CLShoppingCartPrice,
} from '../../common/types/componentLibrary.js';
import type { BasketItem, OfferItem, ShoppingBasketType } from '../../common/types/shoppingBasket.js';
import type { AddOn } from '../../generated/api/addOn.js';
import type { CampaignAssociation } from '../../generated/api/campaignAssociation';
import type { CampaignContext } from '../../generated/api/campaignContext';
import type { CampaignPromotion } from '../../generated/api/campaignPromotion';
import type { CommercialProduct } from '../../generated/api/commercialProduct.js';
import type { EntityPrice } from '../../generated/api/entityPrice';
import type { Offer } from '../../generated/api/offer.js';
import type { OnlineModel } from '../../generated/api/onlineModel.js';
import type { Price } from '../../generated/api/price.js';
import type { ShoppingCartProps } from '@design-system/component-library';

export const isSalesProductModel = (onlineModel?: OnlineModel) =>
  onlineModel?.category === OnlineModelCategory.SALES_PRODUCT;

export const isSelfServiceModel = (onlineModel?: OnlineModel) =>
  onlineModel?.category === OnlineModelCategory.SELF_SERVICE;

export const getCommercialProductForGuid = (guid: string, model?: OnlineModel): CommercialProduct | undefined => {
  return model?.offers
    .flatMap(offer => offer.commercialProducts)
    .find(commercialProduct => commercialProduct?.commercialProductCode === guid);
};

const getEffectivePriceForSalesProduct = (offer: Offer | undefined): PriceData => {
  const commercialProducts = offer?.commercialProducts ?? [];
  const cpWithMonthlyCharge = commercialProducts.find(cp => getEffectiveCharges(cp).monthlyRecurringCharge > 0);
  const cpWithOneTimeCharge = commercialProducts.find(cp => getEffectiveCharges(cp).oneTimeCharge > 0);

  const monthlyCampaignDiscount =
    cpWithMonthlyCharge?.price?.campaign?.campaignAssociation?.monthlyRecurringCharge ?? 0;
  const oneTimeCampaignDiscount = cpWithOneTimeCharge?.price?.campaign?.campaignAssociation?.oneTimeCharge ?? 0;

  return {
    price: {
      monthlyRecurringCharge: cpWithMonthlyCharge
        ? getEffectiveCharges(cpWithMonthlyCharge).monthlyRecurringCharge
        : undefined,
      oneTimeCharge: cpWithOneTimeCharge ? getEffectiveCharges(cpWithOneTimeCharge).oneTimeCharge : undefined,
      payments:
        cpWithMonthlyCharge?.payments ??
        cpWithOneTimeCharge?.payments ??
        commercialProducts.find(cp => cp.payments !== undefined)?.payments,
    },
    isContractPrice: false,
    isCampaignPrice: monthlyCampaignDiscount > 0 || oneTimeCampaignDiscount > 0,
    billingPeriod:
      cpWithMonthlyCharge?.billingPeriod ??
      cpWithOneTimeCharge?.billingPeriod ??
      commercialProducts.find(cp => cp.billingPeriod !== undefined)?.billingPeriod,
  };
};

export const getPriceForSalesProduct = (offer?: Offer): PriceData => {
  return getEffectivePriceForSalesProduct(offer);
};

/**
 * TODO BULK-API: Remove when private/bulk API returns correct campaign pricing for SALES_PRODUCT.
 * For SALES_PRODUCT WiFi items, find the matching offer in a SELF_SERVICE MBB model
 * and return addon-based pricing (which includes campaign discounts).
 * Returns undefined if no matching SELF_SERVICE pricing is found.
 */
const getSalesProductPriceFromSelfService = (
  salesProductModel: OnlineModel,
  salesOffer: Offer | undefined,
  allModels: OnlineModel[]
): PriceData | undefined => {
  if (!salesOffer) {
    return undefined;
  }

  // Find a SELF_SERVICE MBB model among all loaded models
  const mbbModel = allModels.find(m => m.category === OnlineModelCategory.SELF_SERVICE && isMobileBroadbandModel(m));
  if (!mbbModel) {
    return undefined;
  }

  // Match the SALES_PRODUCT offer to a SELF_SERVICE offer by name (case-insensitive due to API inconsistencies)
  const spOfferName = salesOffer.offerName?.toLowerCase();
  const spModelName = salesProductModel.onlineModelName?.toLowerCase();
  const matchingOffer = mbbModel.offers.find(o => {
    const name = o.offerName?.toLowerCase();
    return name === spOfferName || name === spModelName;
  });
  if (!matchingOffer) {
    return undefined;
  }

  const cp = matchingOffer.commercialProducts[0];
  if (!cp) {
    return undefined;
  }

  const mbbPricing = getMobileBroadbandPricing(cp, mbbModel);

  // Only use SELF_SERVICE pricing when there's a campaign discount (effective < list)
  const hasCampaignDiscount =
    mbbPricing.monthlyRecurringCharge < mbbPricing.originalMonthlyRecurringCharge ||
    mbbPricing.oneTimeCharge < mbbPricing.originalOneTimeCharge;
  if (!hasCampaignDiscount) {
    return undefined;
  }

  return {
    price: {
      monthlyRecurringCharge: mbbPricing.monthlyRecurringCharge,
      oneTimeCharge: mbbPricing.oneTimeCharge,
      payments: cp.payments,
    },
    isContractPrice: mbbPricing.isContractPrice,
    isCampaignPrice:
      mbbPricing.monthlyRecurringCharge !== mbbPricing.originalMonthlyRecurringCharge ||
      mbbPricing.oneTimeCharge !== mbbPricing.originalOneTimeCharge,
    billingPeriod: cp.billingPeriod,
  };
};

export const getPriceToDisplay = (priceData: PriceData | undefined, quantity: number): CLShoppingCartPrice => {
  if (priceData?.price?.monthlyRecurringCharge && priceData?.billingPeriod && priceData?.billingPeriod !== 1) {
    return {
      amount: formatSumToString(priceData.price.monthlyRecurringCharge * quantity, true),
      unit: `€/${priceData.billingPeriod}
             ${t.XXVX(monthsMsg)}`,
    };
  }
  if (priceData?.price?.monthlyRecurringCharge) {
    return {
      amount: formatSumToString(priceData.price.monthlyRecurringCharge * quantity, true),
      unit: t.YO7F(sumPerMonthMsg, '€'),
    };
  } else if (priceData?.price?.oneTimeCharge) {
    return { amount: formatSumToString(priceData.price.oneTimeCharge * quantity, true), unit: '€' };
  } else {
    return { amount: formatSumToString(0, true), unit: '€' };
  }
};

export const getPriceFromBasketOfferItem = (offer: OfferItem): Price => {
  return {
    oneTimeCharge: offer.oneTimePriceInCents,
    monthlyRecurringCharge: offer.periodicPrice?.periodicPriceInCents,
    payments: offer.periodicPrice?.payments,
  };
};

export const getEntityPriceForGuid = (guid: string, model: OnlineModel | undefined): EntityPrice | undefined => {
  const commercialProduct = getCommercialProductForGuid(guid, model);
  if (commercialProduct) {
    return commercialProduct.price;
  }
  return undefined;
};

export const getEffectivePriceForGuid = (guid: string, model: OnlineModel | undefined): PriceData | undefined => {
  const entityPrice = getEntityPriceForGuid(guid, model);
  if (entityPrice) {
    return {
      isContractPrice: isContractPrice(entityPrice),
      price: entityPrice.effectivePrice,
    };
  }
  return undefined;
};

export const getOnlineModelForBasketItem = (onlineModels: OnlineModel[], basketItem: BasketItem) => {
  return onlineModels?.find(model => model.onlineModelCode === basketItem.guid);
};

export const getOffer = (offerCode: string, onlineModel: OnlineModel | undefined): Offer | undefined => {
  return onlineModel?.offers.find(offer => offer.offerCode === offerCode);
};

export const getOfferForBasketItem = (onlineModels: OnlineModel[], basketItem: BasketItem): Offer | undefined => {
  const model = getOnlineModelForBasketItem(onlineModels, basketItem);
  return getOffer(basketItem.offer?.guid, model);
};

export const getSalesProductCommercialProductsForOrderItem = (
  basketItem: BasketItem,
  model?: OnlineModel,
  offer?: Offer
): { commercialProductCode: string; additionalCommercialProductCodes?: string[] } => {
  const selected = basketItem.offer.commercialProductGuid;
  if (!isSalesProductModel(model)) {
    return { commercialProductCode: selected };
  }
  const resolved = offer ?? getOffer(basketItem.offer.guid, model);
  const priceOptionCodes = resolved?.priceOptions?.find(option => option.includes(selected));
  if (priceOptionCodes) {
    const additionalCommercialProductCodes = priceOptionCodes.filter(code => code !== selected);
    return {
      commercialProductCode: selected,
      ...(additionalCommercialProductCodes.length ? { additionalCommercialProductCodes } : {}),
    };
  }
  // TODO BULK-API: Remove when priceOptions is populated for Liikkuva WiFi.
  // Hardcoded opening-fee CP fallback since priceOptions is missing for these models.
  const liikkuvaWifiModelCodes: string[] = Object.values(LiikkuvaWifiSalesProductOnlineModel);
  const isLiikkuvaWifi = model && liikkuvaWifiModelCodes.includes(model.onlineModelCode);
  if (isLiikkuvaWifi) {
    return {
      commercialProductCode: selected,
      additionalCommercialProductCodes: [SalesProductOpeningFeeCpCodes.LIIKKUVA_WIFI],
    };
  }
  return { commercialProductCode: selected };
};

export const getPriceForSubscriptionWithPriceType = (
  basketItem: BasketItem,
  onlineModel: OnlineModel,
  priceType: PriceType
): PriceData => {
  const commercialProduct = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, onlineModel);

  const isMobileM2M = isMobileM2MProduct(onlineModel);
  if (isMobileM2M && commercialProduct) {
    // For M2M, find the variant addon (S/M/L/5G) by checking known addon codes
    const m2mVariantCodes = Object.values(MobileM2MAddonCode);
    const allAddonCodes = new Set(basketItem.offer.addOns?.flatMap(item => item.addOnCodes ?? []) ?? []);
    const variantAddonCode = Array.from(allAddonCodes).find(code =>
      m2mVariantCodes.includes(code as MobileM2MAddonCode)
    );

    if (variantAddonCode) {
      const variantAddon = onlineModel.addOns?.find(addon => addon.addOnCode === variantAddonCode);
      if (variantAddon) {
        const oneTime = getM2MOpeningFee(commercialProduct.price?.[priceType]?.oneTimeCharge, onlineModel);
        return {
          price: {
            monthlyRecurringCharge: variantAddon.addOnMonthlyRecurringCharge ?? 0,
            oneTimeCharge: oneTime,
            payments: commercialProduct.payments,
          },
          isContractPrice: false,
          billingPeriod: commercialProduct.billingPeriod,
        };
      }
    }
  }

  // SELF_SERVICE pricing (Voice/MBB)
  const offer = getOffer(basketItem.offer.guid, onlineModel);
  if (commercialProduct) {
    const selfServicePricing = getSelfServiceSubscriptionPrice(commercialProduct, onlineModel);
    if (selfServicePricing) {
      return {
        price: {
          monthlyRecurringCharge:
            priceType === 'effectivePrice'
              ? selfServicePricing.monthlyRecurringCharge
              : selfServicePricing.originalMonthlyRecurringCharge,
          oneTimeCharge:
            priceType === 'effectivePrice'
              ? selfServicePricing.oneTimeCharge
              : selfServicePricing.originalOneTimeCharge,
          payments: commercialProduct.payments,
        },
        isContractPrice: priceType === 'effectivePrice' ? (selfServicePricing.isContractPrice ?? false) : false,
        isCampaignPrice: Boolean(selfServicePricing?.isCampaignPrice),
        billingPeriod: commercialProduct.billingPeriod,
      };
    }
  }

  const priceFromCp = getEntityPriceForGuid(basketItem.offer.commercialProductGuid, onlineModel);
  const priceForType = priceFromCp?.[priceType];
  const oneTimeCharge = priceForType?.oneTimeCharge ?? 0;
  const monthlyRecurringCharge = priceForType?.monthlyRecurringCharge ?? 0;
  if (priceForType && (oneTimeCharge > 0 || monthlyRecurringCharge > 0)) {
    return {
      price: priceForType,
    };
  }

  // Fallback: top-level CP fields
  if (commercialProduct) {
    const topLevelOneTime = commercialProduct.oneTimeCharge ?? 0;
    const topLevelMonthly = commercialProduct.monthlyRecurringCharge ?? 0;
    if (topLevelOneTime > 0 || topLevelMonthly > 0) {
      return {
        price: {
          monthlyRecurringCharge: topLevelMonthly,
          oneTimeCharge: topLevelOneTime,
          payments: commercialProduct.payments,
        },
        billingPeriod: commercialProduct.billingPeriod,
      };
    }
  }

  const mappedAddOns = getBundledOfferAddons(commercialProduct);
  const [recurring, onetime] = (mappedAddOns || []).reduce<[number, number]>(
    (acc, addOn) => {
      const monthly = addOn?.price?.[priceType]?.monthlyRecurringCharge ?? 0;
      const oneTime = addOn?.price?.[priceType]?.oneTimeCharge ?? 0;
      acc[0] += monthly;
      acc[1] += oneTime;
      return acc;
    },
    [0, 0]
  );
  return {
    price: {
      monthlyRecurringCharge: recurring,
      oneTimeCharge: onetime,
      payments: offer?.commercialProducts.find(cp => cp.payments)?.payments,
    },
    billingPeriod: offer?.commercialProducts.find(cp => cp.billingPeriod)?.billingPeriod,
  };
};

export const getEffectivePriceForSubscription = (basketItem: BasketItem, onlineModel: OnlineModel): PriceData => {
  return getPriceForSubscriptionWithPriceType(basketItem, onlineModel, 'effectivePrice');
};

export const getListPriceForSubscription = (basketItem: BasketItem, onlineModel: OnlineModel): PriceData => {
  return getPriceForSubscriptionWithPriceType(basketItem, onlineModel, 'listPrice');
};

export const getListPrice = (basketItem: BasketItem, onlineModel: OnlineModel): Price | undefined => {
  if (isSelfServiceModel(onlineModel)) {
    return getListPriceForSubscription(basketItem, onlineModel).price;
  }
  return getEntityPriceForGuid(basketItem.offer.commercialProductGuid, onlineModel)?.listPrice;
};

export const getEffectivePrice = (
  basketItem: BasketItem,
  onlineModel: OnlineModel | undefined,
  allModels?: OnlineModel[]
) => {
  if (!onlineModel) {
    return undefined;
  }
  switch (onlineModel.category) {
    case OnlineModelCategory.SALES_PRODUCT: {
      const offer = getOffer(basketItem.offer.guid, onlineModel);
      if (allModels) {
        const selfServicePrice = getSalesProductPriceFromSelfService(onlineModel, offer, allModels);
        if (selfServicePrice) {
          return selfServicePrice;
        }
      }
      return getPriceForSalesProduct(offer);
    }
    case OnlineModelCategory.SELF_SERVICE:
      return getEffectivePriceForSubscription(basketItem, onlineModel);
    default:
      return getEffectivePriceForGuid(basketItem.offer.commercialProductGuid, onlineModel);
  }
};

/**
 * Resolves addon price charges from the correct source.
 * Priority: CP associatedAddOns effectivePrice (campaign-adjusted) > model-level deprecated fields > model effectivePrice.
 * For devices (HANDHELD_DEVICE), always uses model-level addon pricing.
 */
export const getAddonPriceCharges = (
  modelAddOn: AddOn,
  commercialProduct?: CommercialProduct,
  model?: OnlineModel
): { oneTimeCharge: number | undefined; monthlyCharge: number | undefined } => {
  // For devices (HANDHELD_DEVICE), use model-level addon pricing (e.g., SquareTrade)
  if (commercialProduct?.productType === CommercialProductType.HANDHELD_DEVICE) {
    return {
      oneTimeCharge: modelAddOn.addOnOneTimeCharge,
      monthlyCharge: modelAddOn.addOnMonthlyRecurringCharge,
    };
  }

  if (commercialProduct) {
    // For subscriptions, use effectivePrice from associatedAddOns (includes campaigns)
    let offerAddOn = commercialProduct.associatedAddOns?.find(a => a.addOnCode === modelAddOn.addOnCode);

    // For MBB (merged Yritysdata + Liikkuva WiFi), if addon not in basketItem's CP, search ALL offers
    const isMobileBroadband = model && isMobileBroadbandModel(model);
    if (!offerAddOn && isMobileBroadband) {
      offerAddOn = model.offers
        ?.flatMap(offer => offer.commercialProducts || [])
        .flatMap(cp => cp.associatedAddOns || [])
        .find(addon => addon.addOnCode === modelAddOn.addOnCode);
    }

    return {
      oneTimeCharge: offerAddOn?.price?.effectivePrice?.oneTimeCharge ?? modelAddOn.addOnOneTimeCharge,
      monthlyCharge:
        offerAddOn?.price?.effectivePrice?.monthlyRecurringCharge ?? modelAddOn.addOnMonthlyRecurringCharge,
    };
  }

  // Fallback when CP is unavailable: use model-level pricing
  return {
    oneTimeCharge: modelAddOn.addOnOneTimeCharge,
    monthlyCharge: modelAddOn.addOnMonthlyRecurringCharge,
  };
};

const getAddonCharge = (
  modelAddOn: AddOn,
  commercialProduct: CommercialProduct | undefined,
  chargeType: 'oneTime' | 'monthly',
  includeMonthlyM2MPrice: boolean = true
): number => {
  if (
    chargeType === 'monthly' &&
    includeMonthlyM2MPrice &&
    commercialProduct &&
    commercialProduct.productType !== CommercialProductType.HANDHELD_DEVICE &&
    Object.values(MobileM2MAddonCode).includes(modelAddOn.addOnCode as MobileM2MAddonCode)
  ) {
    return 0; // M2M variant: monthly price already in periodicPrice
  }

  const { oneTimeCharge, monthlyCharge } = getAddonPriceCharges(modelAddOn, commercialProduct);
  return (chargeType === 'oneTime' ? oneTimeCharge : monthlyCharge) ?? 0;
};

const getDisplayableAddons = (
  addOns: string[],
  model: OnlineModel,
  basketItem: BasketItem
): { modelAddOn: AddOn; addonCount: number }[] => {
  const isMobileM2M = isMobileM2MProduct(model);

  // Track processed addon codes to prevent duplicates from merged models (e.g., MBB = Yritysdata + Liikkuva WiFi)
  const processedAddonCodes = new Set<string>();

  return (
    model.addOns?.flatMap(modelAddOn => {
      // Skip if we've already processed this addon code
      if (processedAddonCodes.has(modelAddOn.addOnCode)) {
        return [];
      }
      processedAddonCodes.add(modelAddOn.addOnCode);

      const commercialProduct = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, model);

      // Hide ALL isOfferAddOn=true addons from basket display.
      // These are auto-included for Salesforce but should not clutter the basket UI.
      // Non-offer addons selected by the user are still shown.
      const isOfferAddon = commercialProduct?.addOnAssociations?.some(
        a => a.addOnCode === modelAddOn.addOnCode && a.isOfferAddOn
      );
      const showAddon = !isOfferAddon;

      // For M2M products, exclude all addons from displayable list (price already includes the variant addon)
      const shouldExcludeM2MAddon = isMobileM2M && addOns.includes(modelAddOn.addOnCode);

      // Only display add-ons that carry an additional price; free / bundled add-ons stay hidden.
      const hasAdditionalPrice =
        getAddonCharge(modelAddOn, commercialProduct, 'monthly') > 0 ||
        getAddonCharge(modelAddOn, commercialProduct, 'oneTime') > 0;

      const addonCount =
        basketItem.offer.addOns?.reduce(
          (count, offerItemAddon) => count + (offerItemAddon.addOnCodes?.includes(modelAddOn.addOnCode) ? 1 : 0),
          0
        ) ?? 0;

      if (addOns.includes(modelAddOn.addOnCode) && showAddon && !shouldExcludeM2MAddon && hasAdditionalPrice) {
        return [
          {
            modelAddOn,
            addonCount,
          },
        ];
      }
      return [];
    }) || []
  );
};

export const calculateAddonPricesOneTime = (
  addOns: { modelAddOn: AddOn; addonCount: number }[],
  commercialProduct?: CommercialProduct
): number =>
  addOns.reduce(
    (total, { modelAddOn, addonCount }) =>
      total + getAddonCharge(modelAddOn, commercialProduct, 'oneTime') * addonCount,
    0
  );

export const calculateAddonPricesMonthly = (
  addOns: { modelAddOn: AddOn; addonCount: number }[],
  commercialProduct?: CommercialProduct,
  includeMonthlyM2MPrice: boolean = true
): number =>
  addOns.reduce(
    (total, { modelAddOn, addonCount }) =>
      total + getAddonCharge(modelAddOn, commercialProduct, 'monthly', includeMonthlyM2MPrice) * addonCount,
    0
  );

export const hasNonMonthlyBillingPeriodItems = (models: OnlineModel[], items?: BasketItem[]): boolean =>
  items?.some(item => {
    const onlineModelForItem = getOnlineModelForBasketItem(models, item);
    const price = onlineModelForItem ? getEffectivePrice(item, onlineModelForItem, models) : undefined;
    return !!price?.billingPeriod;
  }) ?? false;

export const calculateMonthlyAndOneTimeChargesTotals = (
  models: OnlineModel[],
  items: BasketItem[] | undefined,
  basketItemStates: Map<string, BasketItemState> = new Map()
): { monthlyRecurringCharges: number; onetimeCharges: number } => {
  const result = items?.reduce(
    (acc, basketItem) => {
      const onlineModelForItem = getOnlineModelForBasketItem(models, basketItem);
      const basketItemState = basketItemStates.get(basketItem.id) || {};
      const price = basketItemState.showContractPriceWillBeConfirmedMsg
        ? { price: getPriceFromBasketOfferItem(basketItem.offer) }
        : getEffectivePrice(basketItem, onlineModelForItem, models);

      if (!basketItemState.contractPeriodUnavailable || basketItemState.showContractPriceWillBeConfirmedMsg) {
        acc.monthlyRecurringCharges += (price?.price?.monthlyRecurringCharge || 0) * basketItem.quantity;
        acc.onetimeCharges += (price?.price?.oneTimeCharge || 0) * basketItem.quantity;
      }

      const addonsForOfferItem = getAddonsForOfferItem(basketItem.offer);
      if (!onlineModelForItem) {
        return acc;
      }

      const commercialProduct = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, onlineModelForItem);
      const displayedAddons = getDisplayableAddons(addonsForOfferItem, onlineModelForItem, basketItem);
      const addonOneTime = calculateAddonPricesOneTime(displayedAddons, commercialProduct);
      const addonMonthly = calculateAddonPricesMonthly(displayedAddons, commercialProduct);

      acc.onetimeCharges += addonOneTime;
      acc.monthlyRecurringCharges += addonMonthly;

      return acc;
    },
    { monthlyRecurringCharges: 0, onetimeCharges: 0 }
  );

  return result || { monthlyRecurringCharges: 0, onetimeCharges: 0 };
};

export const calculateTotalPrices = (
  models: OnlineModel[],
  items: BasketItem[] | undefined,
  basketItemStates: Map<string, BasketItemState>
): ShoppingCartProps['totals'] => {
  let billingPeriodMultipleMonths = false;
  items?.forEach(basketItem => {
    const onlineModelForItem = getOnlineModelForBasketItem(models, basketItem);
    const basketItemState = basketItemStates.get(basketItem.id) || {};
    const price = basketItemState.showContractPriceWillBeConfirmedMsg
      ? { price: getPriceFromBasketOfferItem(basketItem.offer) }
      : getEffectivePrice(basketItem, onlineModelForItem, models);
    if (onlineModelForItem?.category === OnlineModelCategory.SALES_PRODUCT) {
      if (price?.billingPeriod && price?.billingPeriod !== 1) {
        billingPeriodMultipleMonths = true;
      }
    }
  });

  const { monthlyRecurringCharges, onetimeCharges } = calculateMonthlyAndOneTimeChargesTotals(
    models,
    items,
    basketItemStates
  );

  return [
    ...(monthlyRecurringCharges
      ? [
          {
            name: billingPeriodMultipleMonths ? t.J4WW(recurringChargesMsg) : t.P6BC(monthlyChargesMsg),
            amount: formatSumToString(monthlyRecurringCharges, true),
            unit: '€',
          },
        ]
      : []),
    ...(onetimeCharges
      ? [
          {
            amount: formatSumToString(onetimeCharges, true),
            name: t.GOBY(singleFaresMsg),
            unit: '€',
          },
        ]
      : []),
  ];
};

const getPriceOptionsForOffer = (
  offerCode: string,
  selectedCommercialProduct: string,
  onlineModel: OnlineModel | undefined,
  includeEpp: boolean,
  allowNonEppDevicesWithRecurringCharges: boolean,
  allowNonEppDevicesWithOneTimeCharges: boolean
): PaymentOptionData[] => {
  const offer = getOffer(offerCode, onlineModel);
  return (
    offer?.commercialProducts
      .filter(cp => cp.active)
      .map(cp => ({
        id: cp.commercialProductCode,
        price: cp.price?.effectivePrice,
        isEpp: cp?.productSubType === CommercialProductSubType.EPP_DEVICE,
        selected: cp.commercialProductCode === selectedCommercialProduct,
        hasRecurringCharges: !!cp.price?.effectivePrice.monthlyRecurringCharge,
        hasOneTimeCharges: !!cp.price?.effectivePrice.oneTimeCharge,
      }))
      .filter(paymentOptionData => includeEpp || !paymentOptionData.isEpp)
      .filter(
        paymentOptionData =>
          allowNonEppDevicesWithRecurringCharges || paymentOptionData.isEpp || !paymentOptionData.hasRecurringCharges
      )
      .filter(
        paymentOptionData =>
          allowNonEppDevicesWithOneTimeCharges || paymentOptionData.isEpp || !paymentOptionData.hasOneTimeCharges
      ) || []
  );
};

export const getPaymentLabel = (price: Price | undefined, isEpp: boolean) => {
  if (price?.monthlyRecurringCharge) {
    return `${price.payments}${t.XXVX(monthsMsg)}, ${formatSumToString(price.monthlyRecurringCharge, true)} ${t.YO7F(
      sumPerMonthMsg,
      '€'
    )}${isEpp ? ' (EPP)' : ''}`;
  } else {
    return `${t.ASEI(oneTimePaymentMsg)} ${formatSumToString(price?.oneTimeCharge)}`;
  }
};

export const getPaymentOptions = (
  offerItem: OfferItem,
  onlineModel: OnlineModel | undefined,
  eppSolution?: EppSolutionResponse
): CLShoppingCartPaymentOptions => {
  if (isSalesProductModel(onlineModel)) {
    // no payment options for sales products
    return [];
  }
  const eppActive = eppSolution?.eppSolutionStatus === EppSolutionStatusEnum.ACTIVE;
  const allowNonEppRecurring =
    !eppActive ||
    (eppSolution?.allowDevicesWithRecurringCharges && onlineModel?.category !== OnlineModelCategory.ACCESSORIES);
  const allowNonEppOneTime = !eppActive || eppSolution?.allowDevicesWithOneTimeCharges;
  const priceOptions = getPriceOptionsForOffer(
    offerItem.guid,
    offerItem.commercialProductGuid,
    onlineModel,
    eppActive,
    allowNonEppRecurring === undefined ? true : allowNonEppRecurring,
    allowNonEppOneTime === undefined ? true : allowNonEppOneTime
  );
  return (
    priceOptions
      // Sort lowest monthly charge first
      .sort((a, b) => (a.price?.monthlyRecurringCharge || 0) - (b.price?.monthlyRecurringCharge || 0))
      // Sort EPPs first
      .sort((a, b) => Number(b.isEpp) - Number(a.isEpp))
      // Sort one time charges last
      .sort((a, b) => (a.price?.oneTimeCharge || 0) - (b.price?.oneTimeCharge || 0))
      .map(sortedPrice => ({
        id: sortedPrice.id,
        label: getPaymentLabel(sortedPrice.price, sortedPrice.isEpp) || '',
        selected: sortedPrice.selected,
      }))
  );
};

const getAddonDisclaimers = (addOn: AddOn): string[] => {
  const vatMessage = t.A0OJ(vatPercentageMsg, '0');
  if (isSquareTradeAddOn(addOn)) {
    return [`${t.VWSV(deductibleMsg)} ${squareTradeDeductibleMsg}`, t.B10G(noFixedTermMsg), vatMessage];
  }
  return [vatMessage];
};

export const getAddonPriceDisplayData = (
  modelAddOn: AddOn,
  quantity: number,
  commercialProduct?: CommercialProduct,
  model?: OnlineModel
) => {
  const { oneTimeCharge, monthlyCharge } = getAddonPriceCharges(modelAddOn, commercialProduct, model);

  if (oneTimeCharge) {
    return {
      amount: formatSumToString(oneTimeCharge * quantity, true),
      unit: '€',
    };
  } else if (monthlyCharge) {
    return {
      amount: formatSumToString(monthlyCharge * quantity, true),
      unit: t.YO7F(sumPerMonthMsg, '€'),
    };
  }
  return { amount: '', unit: '' };
};

export const getAddonsDisplayData = (
  addOns: string[],
  model: OnlineModel,
  basketItem: BasketItem
): CLShoppingCartAddon[] => {
  const displayableAddons = getDisplayableAddons(addOns, model, basketItem);
  const commercialProduct = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, model);

  // Note: getDisplayableAddons already deduplicates and sums counts for merged models (lines 394-407)
  return displayableAddons.map(({ modelAddOn, addonCount }) => ({
    disclaimer: getAddonDisclaimers(modelAddOn),
    id: modelAddOn.addOnCode,
    name: modelAddOn.addOnProductName || '',
    price: getAddonPriceDisplayData(modelAddOn, addonCount, commercialProduct, model),
    quantity: addonCount,
    quantitySuffix: addonCount > 1 ? t.BDBD('{} pcs', String(addonCount)) : undefined,
  }));
};

export const getTotalAmount = (basketItems?: BasketItem[]) => {
  return basketItems?.reduce((acc, curr) => acc + curr.quantity, 0) || 0;
};

const findMatchingBasketItem = (
  commercialProductGuid: string,
  existingItem?: BasketItem,
  existingBasketItems?: BasketItem[]
) => {
  return existingBasketItems?.find(
    basketItem =>
      basketItem.offer.commercialProductGuid === commercialProductGuid &&
      deepEqual(getAddonsForOfferItem(existingItem?.offer), getAddonsForOfferItem(basketItem.offer))
  );
};

export const findBasketItemForId = (basketItems: BasketItem[] | undefined, basketItemId: string) => {
  return basketItems?.find(item => item.id === basketItemId);
};

export const changeQuantityInBasket = (shoppingBasket: ShoppingBasketType, basketItemId: string, quantity: number) => {
  const basketItem = findBasketItemForId(shoppingBasket.items, basketItemId);
  if (basketItem) {
    if (quantity === 0) {
      const index = shoppingBasket.items?.findIndex(item => item.id === basketItemId);
      if (typeof index === 'number' && index >= 0) {
        shoppingBasket.items?.splice(index, 1);
      }
    } else {
      basketItem.quantity = quantity;
      if (basketItem.offer.addOns) {
        if (basketItem.quantity > basketItem.offer.addOns.length) {
          const existingAddonCodes = getAddonsForOfferItem(basketItem.offer);
          const existingAttributes = basketItem.offer.addOns[0]?.addOnAttributes;
          basketItem.offer.addOns = [
            ...basketItem.offer.addOns,
            ...createOfferItemAddons(
              basketItem.quantity - basketItem.offer.addOns.length,
              existingAddonCodes.length > 0 ? existingAddonCodes : undefined,
              existingAttributes
            ),
          ];
        } else if (basketItem.quantity < basketItem.offer.addOns.length) {
          basketItem.offer.addOns = basketItem.offer.addOns.slice(0, basketItem.quantity);
        }
      }
    }
  }
  return shoppingBasket;
};

export const replaceAddonsForBasketItem = (
  shoppingBasket: ShoppingBasketType,
  basketItemId: string,
  productIndex: number,
  addons: string[],
  addOnAttributes?: { [addOnCode: string]: { [key: string]: string } }
) => {
  const basketItem = findBasketItemForId(shoppingBasket.items, basketItemId);
  if (!basketItem || !basketItem.offer.addOns || productIndex > basketItem.offer.addOns.length - 1) {
    return shoppingBasket;
  }
  basketItem.offer.addOns[productIndex].addOnCodes = addons;
  basketItem.offer.addOns[productIndex].addOnAttributes =
    addOnAttributes && Object.keys(addOnAttributes).length > 0 ? addOnAttributes : undefined;
  return shoppingBasket;
};

export const setActiveVouchersInBasket = (shoppingBasket: ShoppingBasketType, voucherCodes: string[]) => {
  shoppingBasket.vouchers = voucherCodes;
  return shoppingBasket;
};

const combineQuantitiesForSameItem = (basket: ShoppingBasketType): ShoppingBasketType => {
  const combinedItems: BasketItem[] = [];
  basket.items?.forEach(current => {
    const duplicateIndex = combinedItems?.findIndex(item => areItemsEqualInBasket(item, current));
    if (duplicateIndex !== -1) {
      combinedItems[duplicateIndex].quantity += current.quantity;
      // Extend addOns array to match quantity - each unit needs its own addon entry
      combinedItems[duplicateIndex].offer.addOns = [
        ...(combinedItems[duplicateIndex].offer.addOns ?? []),
        ...(current.offer.addOns ?? []),
      ];
    } else {
      combinedItems.push(current);
    }
  });
  return { ...basket, items: combinedItems };
};

export const removeAddOnFromBasket = (shoppingBasket: string, basketItemId: string, addOnId: string) => {
  const basket: ShoppingBasketType = parseBasketJson(shoppingBasket);
  const item = findBasketItemForId(basket.items, basketItemId);
  if (item?.offer.addOns) {
    item.offer.addOns = item.offer.addOns.filter(offerItemAddon => !offerItemAddon.addOnCodes?.includes(addOnId));
  }

  return JSON.stringify(combineQuantitiesForSameItem(basket));
};

export const changePaymentOption = ({
  basketItem,
  changeToCommercialProductCode,
  onlineModel,
}: ChangePaymentOptions) => {
  const shoppingBasket = parseBasketJson(getShoppingBasketFromLocalStorage()) as ShoppingBasketType;
  const cp = getCommercialProductForGuid(changeToCommercialProductCode, onlineModel);

  // If item has one optional addon, check that it's compatible with the CommercialProduct. If it's not, remove it
  if (getAddonsForOfferItem(basketItem.offer)?.length === 1 && cp) {
    const addOnCode = getAddonsForOfferItem(basketItem.offer)[0];
    const addOnAssociation = cp.addOnAssociations?.find(association => association.addOnCode === addOnCode);
    const firstAssociation = cp.addOnAssociations ? cp.addOnAssociations[0] : undefined;
    if (!addOnAssociation && (!firstAssociation || firstAssociation.addOnPurpose === AddOnPurpose.OPTIONAL)) {
      const itemWithAddon = findBasketItemForId(shoppingBasket.items, basketItem.id);
      if (itemWithAddon) {
        itemWithAddon.offer.addOns = [];
      }
    }
  }
  const itemToChange = findBasketItemForId(shoppingBasket.items, basketItem.id);
  const duplicateItem = findMatchingBasketItem(changeToCommercialProductCode, itemToChange, shoppingBasket?.items);

  if (itemToChange && duplicateItem) {
    changeQuantityInBasket(shoppingBasket, itemToChange.id, duplicateItem.quantity + itemToChange.quantity);
    const indexOfDuplicateItem = shoppingBasket.items?.indexOf(duplicateItem);
    if (indexOfDuplicateItem !== undefined) {
      shoppingBasket.items?.splice(indexOfDuplicateItem, 1);
    }
  }
  if (itemToChange && cp) {
    const price = getEffectivePriceForGuid(cp.commercialProductCode, onlineModel);
    itemToChange.offer.commercialProductGuid = cp.commercialProductCode;
    if (price?.price?.monthlyRecurringCharge) {
      itemToChange.offer.periodicPrice = {
        periodicPriceInCents: price?.price?.monthlyRecurringCharge,
        payments: price?.price?.payments,
        period: cp.billingPeriod || 1, // Billing period in months, usually not available so default to 1
      };
      // Store one-time charge if it exists (e.g., M2M opening fee)
      itemToChange.offer.oneTimePriceInCents = price?.price?.oneTimeCharge;
    } else if (price?.price?.oneTimeCharge) {
      itemToChange.offer.periodicPrice = undefined;
      itemToChange.offer.oneTimePriceInCents = price?.price?.oneTimeCharge;
    }
  }
  return shoppingBasket;
};

export const getOnetimePaymentText = (
  oneTimeCharge: number | undefined,
  quantity: number,
  isActivationFee: boolean
) => {
  const formatOneTimeCharge = formatSum(oneTimeCharge) || '';
  if (isActivationFee) {
    return quantity > 1
      ? t.HWDV(activationFeesWithVarMsg, formatOneTimeCharge)
      : t.HWDU(activationFeeWithVarMsg, formatOneTimeCharge);
  } else {
    return t.ASEI(oneTimePaymentMsg);
  }
};

export const getDisclaimerFields = (oneTimePaymentText: string, priceData?: PriceData, quantity = 1) => {
  const hasOneTimeCharge = !!priceData?.price?.oneTimeCharge;
  const hasMonthlyCharges = !!priceData?.price?.monthlyRecurringCharge;
  const oneTimeChargeFields = ['', oneTimePaymentText, t.A0OJ(vatPercentageMsg, '0')];
  const payments = priceData?.price?.payments || 0;
  // Corporate VAT is 0
  const monthlyRecurringFields = [
    payments === 0 ? '' : t.CF93('{} month agreement', String(payments)),
    payments === 0
      ? ''
      : t.W1RX(
          totalWithCurlyBracketsMsg,
          formatSum(Number(priceData?.price?.monthlyRecurringCharge) * payments * quantity) || ''
        ),
    t.A0OJ(vatPercentageMsg, '0'),
  ];
  if (hasOneTimeCharge || hasMonthlyCharges) {
    return [...(hasOneTimeCharge ? oneTimeChargeFields : []), ...(!hasOneTimeCharge ? monthlyRecurringFields : [])];
  } else {
    return [...oneTimeChargeFields];
  }
};

export const isActiveEppPrice = (cpGuid: string, onlineModel?: OnlineModel) => {
  const commercialProduct = getCommercialProductForGuid(cpGuid, onlineModel);
  return !!(commercialProduct?.active && commercialProduct?.productSubType === CommercialProductSubType.EPP_DEVICE);
};

export const isItemDiscontinued = (basketItem: BasketItem, models: OnlineModel[]) => {
  const availableOnlineModelCodes = models.map(m => m.onlineModelCode);
  return (
    !availableOnlineModelCodes.includes(basketItem.guid) ||
    !getOfferForBasketItem(models, basketItem)?.active ||
    getOnlineModelForBasketItem(models, basketItem)
      ?.offers?.flatMap(o => o.commercialProducts)
      .every(cp => !cp.active) === true
  );
};

export const getBasketItemStates = (
  basketItems: BasketItem[] | undefined,
  models: OnlineModel[],
  eppSolution: EppSolutionResponse | undefined,
  loggedIn: boolean
): Map<string, BasketItemState> => {
  return new Map(
    basketItems?.map(basketItem => {
      const onlineModelForItem = getOnlineModelForBasketItem(models, basketItem);
      const paymentOptions = getPaymentOptions(basketItem.offer, onlineModelForItem, eppSolution);
      const cp = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, onlineModelForItem);
      const isSalesProduct = isSalesProductModel(onlineModelForItem);
      const isContractPeriodUnavailable = isSalesProduct
        ? false
        : !cp ||
          !cp.active ||
          (paymentOptions?.length > 0 && !paymentOptions.find(po => po.id === cp.commercialProductCode));
      const basketItemIsActiveEpp = isActiveEppPrice(basketItem.offer.commercialProductGuid, onlineModelForItem);
      const showContractPriceWillBeConfirmedMsg = !loggedIn && basketItemIsActiveEpp;

      const basketItemState: BasketItemState = {
        contractPeriodUnavailable: isContractPeriodUnavailable,
        showContractPriceWillBeConfirmedMsg: showContractPriceWillBeConfirmedMsg,
      };
      return [basketItem.id, basketItemState];
    })
  );
};

export const getIsVoucherItem = (
  basketItem: BasketItem,
  onlineModel?: OnlineModel,
  activeVoucherCode?: string
): boolean => {
  if (!onlineModel || !activeVoucherCode) {
    return false;
  }

  const offer = getOfferForBasketItem([onlineModel], basketItem);
  const commercialProducts = offer?.commercialProducts;

  // Check if the selected commercial product has a campaign
  // (note that self service subscriptions don't have that, when the campaign is applied)
  const hasCampaign = commercialProducts?.some(
    cp => cp.commercialProductCode === basketItem.offer.commercialProductGuid && cp.price?.campaign
  );
  if (hasCampaign) {
    return true;
  }

  // For self-service products, check if any associated add-on has a discounted price
  if (onlineModel.category === OnlineModelCategory.SELF_SERVICE) {
    const discountedAddOn = commercialProducts
      ?.flatMap(cp => cp.associatedAddOns ?? [])
      .find(addOn => {
        const { listPrice, effectivePrice } = addOn.price ?? {};
        return (
          (listPrice?.oneTimeCharge ?? 0) > (effectivePrice?.oneTimeCharge ?? 0) ||
          (listPrice?.monthlyRecurringCharge ?? 0) > (effectivePrice?.monthlyRecurringCharge ?? 0)
        );
      });

    // When both campaign and voucher are present on product - we need to compare discount amount to decide whether discount is from campaign or voucher
    if (discountedAddOn?.price?.campaign) {
      const rcDiscountAmount =
        (discountedAddOn?.price?.listPrice?.monthlyRecurringCharge ?? 0) -
        (discountedAddOn.price.effectivePrice?.monthlyRecurringCharge ?? 0);
      const rcCampaignDiscountAmount = discountedAddOn.price?.campaign.campaignAssociation.monthlyRecurringCharge ?? 0;
      return rcDiscountAmount !== rcCampaignDiscountAmount;
    }
    return !!discountedAddOn;
  }

  return false;
};

export const getBadge = (
  isVoucherItem: boolean,
  isLoggedIn: boolean,
  priceData?: PriceData,
  activeVoucherCode?: string
) => {
  if (!isLoggedIn) {
    return undefined;
  }
  if (isVoucherItem) {
    return { text: activeVoucherCode! };
  }
  if (priceData?.isCampaignPrice) {
    return { text: t.TJPQ('Offer'), subtle: true };
  }
  if (priceData?.isContractPrice) {
    return { text: t.JPKP(contractPriceCapitalizedMsg), subtle: true };
  }
  return undefined;
};

export const getDisclaimerStrings = (oneTimePaymentText: string, priceData?: PriceData, quantity = 1): string[] => {
  const vat = t.A0OJ(vatPercentageMsg, '0');
  const hasOneTimeCharge = !!priceData?.price?.oneTimeCharge;
  const hasMonthlyCharges = !!priceData?.price?.monthlyRecurringCharge;
  const payments = priceData?.price?.payments || 0;

  if (hasOneTimeCharge) {
    return [oneTimePaymentText, vat];
  }
  if (hasMonthlyCharges && payments > 0) {
    const totalPrice = formatSum(Number(priceData?.price?.monthlyRecurringCharge) * payments * quantity) || '';
    return [t.CF93('{} month agreement', String(payments)), t.W1RX(totalWithCurlyBracketsMsg, totalPrice), vat];
  }
  if (priceData?.price?.oneTimeCharge === 0 && priceData?.price?.monthlyRecurringCharge === 0) {
    return [oneTimePaymentText, vat];
  }
  return [vat];
};

export const getCampaignAssociation = (
  voucherCode: string,
  campaignPromotions: CampaignPromotion[],
  campaignContexts: CampaignContext[],
  onlineModel?: OnlineModel,
  offer?: Offer,
  commercialProductCode?: string
) => {
  const allCampaignPromotions = [...campaignPromotions, ...(onlineModel?.campaigns || [])];

  if (offer) {
    const offerCampaignAssociationAndPromotion = getOfferCampaignAssociationAndPromotion(
      offer,
      SalesType.NEW_SALE,
      campaignContexts,
      voucherCode,
      allCampaignPromotions,
      commercialProductCode
    );

    if (offerCampaignAssociationAndPromotion?.campaignAssociation) {
      return offerCampaignAssociationAndPromotion.campaignAssociation;
    }

    // Fallback for sales products where campaign data is present under cp.price.campaign
    const selectedCp = commercialProductCode
      ? offer.commercialProducts.find(cp => cp.commercialProductCode === commercialProductCode)
      : offer.commercialProducts[0];
    const campaignAssociationFromEntityPrice = selectedCp?.price?.campaign?.campaignAssociation;

    return campaignAssociationFromEntityPrice;
  }
  return undefined;
};

export const getOfferFixedTermPeriod = (offer?: Offer, campaignAssociation?: CampaignAssociation) => {
  if (campaignAssociation?.fixedTermPeriod) {
    return campaignAssociation.fixedTermPeriod;
  }

  if (!offer) {
    return undefined;
  }

  return offer.commercialProducts
    .flatMap(commercialProduct => [
      ...(commercialProduct.campaignAssociations ?? []),
      ...(commercialProduct.price?.campaign?.campaignAssociation
        ? [commercialProduct.price.campaign.campaignAssociation]
        : []),
      ...(commercialProduct.associatedAddOns?.flatMap(addOn =>
        addOn.price?.campaign?.campaignAssociation ? [addOn.price.campaign.campaignAssociation] : []
      ) ?? []),
    ])
    .find(foundCampaignAssociation => foundCampaignAssociation.fixedTermPeriod)?.fixedTermPeriod;
};
